import { I18n } from "@aws-amplify/core";
import { get, filter, size, forEach, includes, isEmpty } from "lodash";

import { fetchPost, fetchGet } from "../api/fetch";

import { fetchLogsTab } from "../actions/siteEntranceExit/tabs";

import { EVENT_TYPE, EVENT_ATTR } from "../constants/events";

import { getLov } from "../utils/lov";
import { getUser } from "../utils/users";
import { formatDate } from "../utils/date";
import { findEventTypeInTree } from "../utils/event";
import { getLovValue, getAttributeValue } from "../utils/profile";
import { getThreshold, getTemperatureByOperation } from "../utils/temperature";

import {
  eventGroupsData,
  COVID_VERIFICATION,
  VERIFICATION_OK,
  VERIFICATION_WARNING,
  VERIFICATION_THREAT,
  EVENT_GROUP_ATTR,
} from "../constants/eventGroups";
import { driveInOut, walkInOut, trainInOut } from "../constants/reducers";
import {
  driver,
  human,
  truck,
  trailer,
  trailer1,
  trailer2,
  container,
  container1,
  container2,
  passenger,
  railcars,
} from "../constants/common";

import { driveInOutFields, trainInOutFields, walkInOutFields } from "../resources/SiteLogFields";

import colors from "../assets/style/colors";

export const isEventGroup = (id) => {
  if (id && !isNaN(id)) {
    return true;
  }
  return false;
};

export const fetchLogs = (
  dispatch,
  name,
  tabId,
  zoneIds,
  types,
  limit,
  offset,
  force
) => {
  dispatch(
    fetchLogsTab(name, tabId, zoneIds, types, {
      offset: offset || 0,
      limit,
      force: force !== null ? force : offset ? true : false,
    })
  );
};

export const fetchLog = async (name, filter, language, pagination = {}) => {
  try {
    let body = {
      filter,
      pagination: {
        offset: pagination.offset || 0,
      },
      data: eventGroupsData,
    };
    if (pagination.limit) {
      body.pagination.limit = pagination.limit;
    } else {
      body.pagination.limit = 2147483647;
    }
    console.log(`fetchLog getting event_group(s): ${JSON.stringify(filter)}.`);
    let response = await fetchPost("new/event_groups", body);
    let parentFilter = {
      parent_ids: response.items.map((item) => item.id),
    };
    console.log(`fetchLog getting event_group(s): ${JSON.stringify(parentFilter)} children.`);
    let childEventGroups = await fetchPost("new/event_groups", {
      filter: parentFilter,
      pagination: {
        "limit": 2147483647,
        "offset": 0
      },
      data: eventGroupsData,
    });

    let logData = await extractLogData(name, response.items, childEventGroups, language);

    return logData;
  } catch (e) {
    console.log(e);
  }
};

const extractLogData = async (name, eventGroups, childEventGroups, language) => {
  let logData = await extractDACInfo(name, eventGroups, language);
  logData = addChildEventGroups(
    name,
    logData,
    childEventGroups.items.filter((i) => i.parent.id === logData.id)
  );

  return logData;
};

export const extractDriveInOutInfo = (data, getState, lov) => {
  let language = get(getState(), ["user", "language"]);
  let group = {
    complete: data,
    id: data.id,
    trailerSeq: 1,
    main: {
      active: data.active,
      ref: data.id,
      notes: data.notes,
      processedBy: data.created_by,
      comments: data.description,
      status: get(data, ["status", `name_${language}`]),
      riskLevel: getLovValue("riskLevel", data.risk_level, lov, language),
    },
    driver: {},
    passenger: {},
    truck: {},
    trailer1: {},
    trailer2: {},
    container1: {},
    container2: {},
  };

  if (data.events) {
    // Find driver event
    let humanEvents = filter(
      get(data, ["events"]),
      (e) =>
        get(e, ["profile", "type", "id"]) === 1 &&
        includes([60, 61], get(e, ["type", "id"]))
    );
    let driverEvent;
    forEach(humanEvents, (he) => {
      let isDriver = getAttributeValue(he, 102);
      if (isDriver) {
        driverEvent = he;
      }
    });

    data.events.forEach((e) => {
      if (e.active) {
        if (!group.main.logType && [60, 61].includes(get(e, ["type", "id"]))) {
          group.main.logType = get(e, ["type", `name_${language}`]) || "";
          group.main.zone = get(e, ["zone", "name"]) || "";
          group.main.zoneId = get(e, ["zone", "id"]) || "";
          group.main.site = get(e, ["zone", "site", "name"]) || "";
          group.main.siteId = get(e, ["zone", "site", "id"]) || "";

          forEach(get(e, ["attributes"]), (attr) => {
            if (attr.id === 105) {
              group.main.entryStatus = getLovValue(
                attr.id,
                attr.value,
                lov,
                language
              );
            }
          });
        }
        if (
          e.profile &&
          e.profile.type &&
          includes([60, 61], get(e, ["type", "id"]))
        ) {
          // Human Profile
          if (e.profile.type.id === 1) {
            let pType =
              e.id === get(driverEvent, ["id"]) || !get(driverEvent, ["id"])
                ? driver
                : passenger;

            if (e.type.id === 60 || e.type.id === 61) {
              group[pType].notes = e.notes;
            }
            // Check for override
            if (e.type.id === 55) {
              group[pType].overriden = true;
              // Check for most recent override
              if (
                !group[pType].overridenOn ||
                group[pType].overridenOn < e.created_on
              ) {
                group[pType].overridenBy = e.created_by;
                group[pType].overridenOn = e.created_on;
              }
            }
            // Check event group update
            if (
              !group[pType].id ||
              (e.type.id === 55 && e.created_on > group[pType].createdOn)
            ) {
              group[pType].id = e.id;
              group[pType].createdOn = e.created_on;
              group[pType].profile = e.profile;
              group[pType].name = `${getAttributeValue(
                e.profile,
                31
              )} ${getAttributeValue(e.profile, 32)}`;
              group[pType].tag = get(e, ["profile", "tag", `name_${language}`]);
              group[pType].tagColor = get(e, ["profile", "tag", "color_rgb"]);
              group[pType].threatLevel = get(e, [
                "profile",
                "tag",
                "threat_flag",
              ])
                ? I18n.get("site_yes")
                : I18n.get("site_no");
            }
          }
          // Vehicule Profile
          else if (e.profile.type.id === 2) {
            if (e.type.id === 60 || e.type.id === 61) {
              group.main.modifiedOn = formatDate(e.created_on);
              group.truck.notes = e.notes;
            }
            // Check for override
            if (e.type.id === 55) {
              group.truck.overriden = true;
              // Check for most recent override
              if (
                !group.truck.overridenOn ||
                group.truck.overridenOn < e.created_on
              ) {
                group.truck.overridenBy = e.created_by;
                group.truck.overridenOn = e.created_on;
              }
            }
            // Check event group update
            if (
              !group.truck.id ||
              (e.type.id === 55 && e.created_on > group.truck.createdOn)
            ) {
              group.truck.id = e.id;
              group.truck.createdOn = e.created_on;
              forEach(get(e, ["attributes"]), (attr) => {
                if (attr.id === 89) {
                  group.main.carrier = getLovValue(
                    attr.id,
                    attr.value,
                    lov,
                    language
                  );
                  group.truck.carrier = group.main.carrier;
                } else if (attr.id === 106) {
                  group.truck.privateVehicle =
                    getLovValue(attr.id, attr.value, lov, language) || "No";
                } else if (attr.id === 139) {
                  group.main.tailgating = attr.value || false;
                } else if (attr.id === 107) {
                  group.truck.currentConfiguration = attr.value;
                } else if (attr.id === 110) {
                  group.truck.department = attr.value;
                }
                // Main date time is determined by the truck date time
                else if (attr.id === 76 || attr.id === 77) {
                  group.main.dateTime = formatDate(attr.value);
                  if (attr.value === "Invalid date") {
                    group.main.dateTime = "";
                  }
                }
              });
              e.profile.attributes.forEach((attr) => {
                if (attr.id === 18) {
                  group.truck.name = attr.value;
                } else if (attr.id === 42) {
                  group.truck.vehicleType = getLovValue(
                    attr.id,
                    attr.value,
                    lov,
                    getState().user.language
                  );
                } else if (attr.id === 44) {
                  group.truck.licensePlate = attr.value;
                } else if (attr.id === 45) {
                  group.truck.make = attr.value;
                } else if (attr.id === 46) {
                  group.truck.model = attr.value;
                }
              });
              group.truck.profile = e.profile;
            }
          }
          // Trailer Profile
          else if (e.profile.type.id === 3) {
            group.trailerSeq = getAttributeValue(e, 121) || 1;

            if (!group[trailer + group.trailerSeq]) {
              group[trailer + group.trailerSeq] = {};
            }

            if (e.type.id === 60 || e.type.id === 61) {
              group[trailer + group.trailerSeq].notes = e.notes;
            }
            // Check for override
            if (e.type.id === 55) {
              group[trailer + group.trailerSeq].overriden = true;
              // Check for most recent override
              if (
                !group[trailer + group.trailerSeq].overridenOn ||
                group[trailer + group.trailerSeq].overridenOn < e.created_on
              ) {
                group[trailer + group.trailerSeq].overridenBy = e.created_by;
                group[trailer + group.trailerSeq].overridenOn = e.created_on;
              }
            }
            // Check event group update
            if (
              !group[trailer + group.trailerSeq].id ||
              (e.type.id === 55 &&
                e.created_on > group[trailer + group.trailerSeq].createdOn)
            ) {
              e.profile.attributes.forEach((attr) => {
                if (attr.id === 18) {
                  group[trailer + group.trailerSeq].name = attr.value;
                } else if (attr.id === 53) {
                  group[trailer + group.trailerSeq].trailerType = getLovValue(
                    attr.id,
                    attr.value,
                    lov,
                    getState().user.language
                  );
                } else if (attr.id === 44) {
                  group[trailer + group.trailerSeq].licensePlate = attr.value;
                } else if (attr.id === 45) {
                  group[trailer + group.trailerSeq].make = attr.value;
                } else if (attr.id === 46) {
                  group[trailer + group.trailerSeq].model = attr.value;
                } else if (attr.id === 89) {
                  group[trailer + group.trailerSeq].carrier = getLovValue(
                    attr.id,
                    attr.value,
                    lov,
                    getState().user.language
                  );
                }
              });
              group[trailer + group.trailerSeq].id = e.id;
              group[trailer + group.trailerSeq].profile = e.profile;
              group[trailer + group.trailerSeq].createdOn = e.created_on;
            }
          }
          // Container Profile
          else if (e.profile.type.id === 4) {
            group.containerSeq = getAttributeValue(e, 121) || 1;

            if (!group[container + group.containerSeq]) {
              group[container + group.containerSeq] = {};
            }

            if (e.type.id === 60 || e.type.id === 61) {
              group[container + group.containerSeq].notes = e.notes;
            }
            // Check for override
            if (e.type.id === 55) {
              group[container + group.containerSeq].overriden = true;
              // Check for most recent override
              if (
                !group[container + group.trailerSeq].overridenOn ||
                group[container + group.trailerSeq].overridenOn < e.created_on
              ) {
                group[container + group.containerSeq].overridenBy =
                  e.created_by;
                group[container + group.containerSeq].overridenOn =
                  e.created_on;
              }
            }
            // Check event group update
            if (
              !group[container + group.containerSeq].id ||
              (e.type.id === 55 &&
                e.created_on > group[container + group.containerSeq].createdOn)
            ) {
              e.profile.attributes.forEach((attr) => {
                if (attr.id === 18) {
                  group[container + group.containerSeq].name = attr.value;
                } else if (attr.id === 53) {
                  group[
                    container + group.containerSeq
                  ].trailerType = getLovValue(
                    attr.id,
                    attr.value,
                    lov,
                    getState().user.language
                  );
                } else if (attr.id === 44) {
                  group[container + group.containerSeq].licensePlate =
                    attr.value;
                } else if (attr.id === 45) {
                  group[container + group.containerSeq].make = attr.value;
                } else if (attr.id === 46) {
                  group[container + group.containerSeq].model = attr.value;
                } else if (attr.id === 89) {
                  group[container + group.containerSeq].carrier = getLovValue(
                    attr.id,
                    attr.value,
                    lov,
                    getState().user.language
                  );
                }
              });
              group[container + group.containerSeq].id = e.id;
              group[container + group.containerSeq].profile = e.profile;
              group[container + group.containerSeq].createdOn = e.created_on;
            }
          }
          // Verification data
          if (e.profile.type.id === 2 || e.profile.type.id === 3) {
            // Verifications order: fuel, odometer, seal, temperatures
            // Temperatures order: verbal, displayed, measured
            let newTrailer = {
              verified: [false, false, false, false],
              tempFront: ["", "", ""],
              tempMiddle: ["", "", ""],
              tempBack: ["", "", ""],
              //profile: e.profile
            };
            forEach(get(e, ["attributes"]), (attr) => {
              if (attr.id === 78) {
                newTrailer.po = attr.value;
              } else if (attr.id === 120) {
                newTrailer.bol = attr.value;
              } else if (attr.id === 109) {
                newTrailer.product = attr.value;
              } else if (attr.id === 108) {
                newTrailer.loaded = getLovValue(
                  attr.id,
                  attr.value,
                  lov,
                  getState().user.language
                );
              }
            });
            if (e.events && e.events.length > 0) {
              e.events.forEach((ev) => {
                if (ev.attributes && ev.attributes.length > 0) {
                  ev.attributes.forEach((attr) => {
                    if (attr.id === 79) {
                      newTrailer.verified[0] = attr.value ? true : false;
                      newTrailer.fuelLevel = getLovValue(
                        attr.id,
                        attr.value,
                        lov,
                        getState().user.language
                      );
                    } else if (attr.id === 19) {
                      newTrailer.comments = attr.value;
                    } else if (attr.id === 83) {
                      newTrailer.odometer = attr.value;
                      newTrailer.verified[1] = attr.value ? true : false;
                    } else if (attr.id === 81) {
                      newTrailer.seal = attr.value;
                      newTrailer.verified[2] = attr.value ? true : false;
                    } else if (attr.id === 88) {
                      newTrailer.tempType = getLovValue(
                        attr.id,
                        attr.value,
                        lov,
                        getState().user.language
                      );
                    } else if (attr.id === 80) {
                      newTrailer.tempFront[0] = attr.value;
                      newTrailer.verified[3] = attr.value
                        ? true
                        : newTrailer.verified[3];
                    } else if (attr.id === 112) {
                      newTrailer.tempMiddle[0] = attr.value;
                      newTrailer.verified[3] = attr.value
                        ? true
                        : newTrailer.verified[3];
                    } else if (attr.id === 113) {
                      newTrailer.tempBack[0] = attr.value;
                      newTrailer.verified[3] = attr.value
                        ? true
                        : newTrailer.verified[3];
                    } else if (attr.id === 114) {
                      newTrailer.tempFront[1] = attr.value;
                      newTrailer.verified[3] = attr.value
                        ? true
                        : newTrailer.verified[3];
                    } else if (attr.id === 115) {
                      newTrailer.tempMiddle[1] = attr.value;
                      newTrailer.verified[3] = attr.value
                        ? true
                        : newTrailer.verified[3];
                    } else if (attr.id === 116) {
                      newTrailer.tempBack[1] = attr.value;
                      newTrailer.verified[3] = attr.value
                        ? true
                        : newTrailer.verified[3];
                    } else if (attr.id === 117) {
                      newTrailer.tempFront[2] = attr.value;
                      newTrailer.verified[3] = attr.value
                        ? true
                        : newTrailer.verified[3];
                    } else if (attr.id === 118) {
                      newTrailer.tempMiddle[2] = attr.value;
                      newTrailer.verified[3] = attr.value
                        ? true
                        : newTrailer.verified[3];
                    } else if (attr.id === 119) {
                      newTrailer.tempBack[2] = attr.value;
                      newTrailer.verified[3] = attr.value
                        ? true
                        : newTrailer.verified[3];
                    }
                  });
                }
              });
            }
            if (e.profile.type.id === 2) {
              group.truck = { ...group.truck, ...newTrailer };
            } else if (e.profile.type.id === 3) {
              group[trailer + group.trailerSeq] = {
                ...group[trailer + group.trailerSeq],
                ...newTrailer,
              };
            }
          }
        }
      }
    });
  }
  return group;
};

export const extractWalkInOutInfo = (data, getState, lov) => {
  let group = {
    complete: data,
    id: data.id,
    main: {
      active: data.active,
      ref: data.id,
      notes: data.notes,
      processedBy: data.created_by,
      comments: data.description,
      status: get(data, ["status", `name_${getState().user.language}`]),
      riskLevel: getLovValue(
        "riskLevel",
        data.risk_level,
        lov,
        getState().user.language
      ),
    },
    human: {},
  };
  if (data.events) {
    data.events.forEach((e) => {
      if (e.active) {
        if (!group.main.logType && [58, 59].includes(get(e, ["type", "id"]))) {
          group.main.logType =
            get(e, ["type", `name_${getState().user.language}`]) || "";
          group.main.zone = get(e, ["zone", "name"]) || "";
          group.main.zoneId = get(e, ["zone", "id"]) || "";
          group.main.site = get(e, ["zone", "site", "name"]) || "";
          group.main.siteId = get(e, ["zone", "site", "id"]) || "";
          group.main.modifiedOn = formatDate(e.created_on);

          forEach(get(e, ["attributes"]), (attr) => {
            if (attr.id === 105) {
              group.main.entryStatus = getLovValue(
                attr.id,
                attr.value,
                lov,
                getState().user.language
              );
              group.main.entryStatusId = attr.value;
            }
          });
        }
        if (
          e.profile &&
          get(e, ["profile", "type", "id"]) === 1 &&
          includes([58, 59], get(e, ["type", "id"]))
        ) {
          if ([58, 59].includes(get(e, ["type", "id"]))) {
            group.human.notes = e.notes;
          }
          // Check for override
          if (get(e, ["type", "id"]) === 55) {
            group.human.overriden = true;
            // Check for most recent override
            if (
              !group.human.overridenOn ||
              group.human.overridenOn < e.created_on
            ) {
              group.human.overridenBy = e.created_by;
              group.human.overridenOn = e.created_on;
            }
          }
          // Check event group update
          if (
            !group.human.id ||
            (e.type.id === 55 && e.created_on > group.human.createdOn)
          ) {
            group.main.dateTime = formatDate(
              getAttributeValue(e, 76) || getAttributeValue(e, 77)
            );
            group.human.id = e.id;
            group.human.createdOn = e.created_on;
            group.human.profile = e.profile;
            group.human.name = `${getAttributeValue(
              e.profile,
              31
            )} ${getAttributeValue(e.profile, 32)}`;
            group.human.tag = get(e, [
              "profile",
              "tag",
              `name_${getState().user.language}`,
            ]);
            group.human.tagColor = get(e, ["profile", "tag", "color_rgb"]);
            group.human.threatLevel = get(e, ["profile", "tag", "threat_flag"])
              ? I18n.get("site_yes")
              : I18n.get("site_no");
          }
        }
      }
    });
  }
  return group;
};

export const extractTrainInOutInfo = (data, getState, lov) => {
  let language = get(getState(), ["user", "language"]);

  let group = {
    complete: data,
    id: data.id,
    trailerSeq: 1,
    main: {
      active: data.active,
      ref: data.id,
      notes: data.notes,
      processedBy: data.created_by,
      comments: data.description,
      status: get(data, ["status", `name_${language}`]),
      riskLevel: getLovValue("riskLevel", data.risk_level, lov, language),
    },
    railcars: [],
  };

  if (data.attributes) {
    forEach(get(data, ["attributes"]), (attr) => {
      if (attr.id === EVENT_GROUP_ATTR.TRAIN_ID) {
        group.main.trainId = attr.value;
      }
      if (attr.id === EVENT_GROUP_ATTR.RAIL_DETECTION_TYPE) {
        group.main.railDetectionType = getLovValue(
          attr.id,
          attr.value,
          lov,
          getState().user.language
        );
      }
      if (attr.id === EVENT_GROUP_ATTR.YARD_EVENT_TYPE) {
        group.main.yardEventType = getLovValue(
          attr.id,
          attr.value,
          lov,
          getState().user.language
        );
      }
    });
  }

  if (data.events) {
    data.events.forEach((e) => {
      let event_type_id = get(e, ["type", "id"]);
      if (e.active) {
        // TODO: change this for RAIL_IN OR RAIL_OUT results
        if (!group.main.logType && [EVENT_TYPE.EDGE_RAILROAD_DETECTION].includes(event_type_id)) {
          group.main.logType = get(e, ["type", `name_${language}`]) || "";
          group.main.zone = get(e, ["zone", "name"]) || "";
          group.main.zoneId = get(e, ["zone", "id"]) || "";
          group.main.site = get(e, ["zone", "site", "name"]) || "";
          group.main.siteId = get(e, ["zone", "site", "id"]) || "";
        }
        if (event_type_id === EVENT_TYPE.EDGE_RAILROAD_DETECTION) {
          forEach(get(e, ["attributes"]), (attr) => {
            if (attr.id === EVENT_ATTR.CAMERA_TIMESTAMP) {
              group.main.dateTime = formatDate(attr.value);
              if (attr.value === "Invalid date") {
                group.main.dateTime = "";
              }
            }
          });
        }
      }
    });
  }

  return group;
}

export const extractDACInfo = async (name, dacData, language) => {
  try {
    let lov = await getDacLov(name);
    let eventGroups = [];
    dacData.forEach((data) => {
      let getState = () => ({ user: { language } });
      let group;
      switch(name) {
        case driveInOut:
          group = extractDriveInOutInfo(data, getState, lov);
          break;
        case walkInOut:
          group = extractWalkInOutInfo(data, getState, lov);
          break;
        case trainInOut:
          group = extractTrainInOutInfo(data, getState, lov);
          break;
      }
      eventGroups.push(group);
    });

    // Fetch processed by users
    for (var i = 0; i < eventGroups.length; i++) {
      if (eventGroups[i].main.processedBy) {
        let response = await getUser(eventGroups[i].main.processedBy);
        let processedBy =
          response.profile.firstname + " " + response.profile.lastname;
        eventGroups[i].main.processedBy = processedBy;
      }
    }

    // Fetch overriden by users
    await fetchOverridenByUsers(eventGroups);

    return eventGroups[0];
  } catch (e) {
    console.log(e);
  }
};

export const extractDACInfo2 = async (name, dacData, language) => {
  try {
    let lov = await getDacLov(driveInOut);
    let eventGroups = [];
    dacData.forEach((data) => {
      let getState = () => ({ user: { language } });
      let group =
        name === driveInOut
          ? extractDriveInOutInfo(data, getState, lov)
          : extractWalkInOutInfo(data, getState, lov);
      eventGroups.push(group);
    });
    return eventGroups;
  } catch (e) {
    console.log(e);
  }
};

// Fetch lov values for DAC event group
export const getDacLov = async (name) => {
  let lov = {};
  let subFields;
  let verified;
  switch (name) {
    case driveInOut:
      subFields = driveInOutFields.subFields || {};
      verified = driveInOutFields.verified || [];
      break;
    case walkInOut:
      subFields = walkInOutFields.subFields || {};
      verified = walkInOutFields.verified || [];
      break;
    case trainInOut:
      subFields = trainInOutFields.subFields || {};
      verified = trainInOutFields.verified || [];
      break;
  }
  let keys = Object.keys(subFields);
  for (let k in keys) {
    for (let i = 0; i < subFields[k].length; i++) {
      if (subFields[k][i].type === "lov") {
        if (subFields[k][i].id) {
          lov[subFields[k][i].id] = await getLov(subFields[k][i].id);
        } else {
          if (subFields[k][i].name === "riskLevel") {
            lov[subFields[k][i].name] = await getLov(
              subFields[k][i].name,
              false
            );
          }
        }
      }
    }
  }
  for (let i = 0; i < verified.length; i++) {
    if (verified[i].type === "lov") {
      lov[verified[i].id] = await getLov(verified[i].id);
    }
  }
  return lov;
};

export const addChildEventGroups = (
  name,
  group,
  childEventGroups,
  lov,
  language
) => {
  if (name === driveInOut) {
    group.detections = {
      driver: {},
      passenger: {},
      truck: {},
      trailer1: {},
      trailer2: {},
      container1: {},
      container2: {},
    };
  } else if (name === walkInOut) {
    group.detections = {
      human: {},
    };
  } else if (name === trainInOut) {
    group.detections = {
      railcars: [],
      trailers: [],
      containers: [],
      others: []
    }
  }
  if (childEventGroups) {
    forEach(
      filter(childEventGroups, (eg) => eg.active),
      (detectionEventGroup) => {
        if (get(detectionEventGroup, ["type", "id"]) === 1) {
          if (name === driveInOut) {
            let associatedHumanEvent = get(
              filter(
                get(group, ["complete", "events"]),
                (e) =>
                  includes([60, 61], get(e, ["type", "id"])) &&
                  get(e, ["profile", "id"]) ===
                    get(detectionEventGroup, ["profile", "id"])
              ),
              [0]
            );
            let isDriver = getAttributeValue(associatedHumanEvent, 102);
            group.detections[
              isDriver === false ? passenger : driver
            ] = detectionEventGroup;
            //group.detections[driver] = detectionEventGroup;
          }
          if (name === walkInOut) {
            group.detections.human = detectionEventGroup;
            // Wearing Mask?
            const faceDetectionEvents = findEventTypeInTree(
              detectionEventGroup,
              [2]
            );
            group.human.wearingMask = null;
            forEach(faceDetectionEvents, (e) => {
              if (getAttributeValue(e, 152) === true) {
                group.human.wearingMask = true;
              } else if (
                group.human.wearingMask === null &&
                getAttributeValue(e, 152) === false
              ) {
                group.human.wearingMask = false;
              }
            });
          }
        } else if (get(detectionEventGroup, ["type", "id"]) === 10) {
          group.detections.truck = detectionEventGroup;
        } else if (get(detectionEventGroup, ["type", "id"]) === 11) {
          if (name === trainInOut) {
            group.detections["trailers"].push(detectionEventGroup);
          } else {
            let trailerSeq = trailer1;
            let associatedTrailerEvent = get(
              filter(
                get(group, ["complete", "events"]),
                (e) =>
                  includes([60, 61], get(e, ["type", "id"])) &&
                  get(e, ["profile", "id"]) ===
                    get(detectionEventGroup, ["profile", "id"])
              ),
              [0]
            );
            let seqNumb = getAttributeValue(associatedTrailerEvent, 121);
            if (seqNumb === 2) trailerSeq = trailer2;
            group.detections[trailerSeq] = detectionEventGroup;
          }
        } else if (get(detectionEventGroup, ["type", "id"]) === 12) {
          if (name === trainInOut) {
            group.detections["containers"].push(detectionEventGroup);
          } else {
            let containerSeq = container1;
            let associatedContainerEvent = get(
              filter(
                get(group, ["complete", "events"]),
                (e) =>
                  includes([60, 61], get(e, ["type", "id"])) &&
                  get(e, ["profile", "id"]) ===
                    get(detectionEventGroup, ["profile", "id"])
              ),
              [0]
            );
            let seqNumb = getAttributeValue(associatedContainerEvent, 121);
            if (seqNumb === 2) containerSeq = container2;
            group.detections[containerSeq] = detectionEventGroup;
          }
        } else if (get(detectionEventGroup, ["type", "id"]) === 13) {
          if (name === walkInOut) {
            group.measuredTemperature = detectionEventGroup;
            const maxTemperature = getTemperatureByOperation(
              detectionEventGroup,
              "max"
            );
            const threshold = getThreshold(detectionEventGroup);
            group.human.temperatureStatus =
              maxTemperature < threshold ? "green" : "red";
          }
        } else if (get(detectionEventGroup, ["type", "id"]) === 14) {
          if (
            isEmpty(group.verification) ||
            get(group, ["verification", "created_on"]) <
              get(detectionEventGroup, ["created_on"])
          ) {
            group.verification = detectionEventGroup;
            if (get(detectionEventGroup, ["events"])) {
              let covidSurvey = get(detectionEventGroup, ["events"]).filter(
                (e) => get(e, ["type", "id"]) === 81
              );
              let preSurvey = get(detectionEventGroup, ["events"]).filter(
                (e) => get(e, ["type", "id"]) === 91
              );

              if (covidSurvey && covidSurvey.length > 0) {
                covidSurvey = covidSurvey[0];
                let trueResponse = get(covidSurvey, ["attributes"]).filter(
                  (attr) => attr.value === true
                );
                let lukeAction =
                  get(
                    filter(
                      get(covidSurvey, ["events"]),
                      (e) => get(e, ["type", "id"]) === 88
                    ),
                    [0]
                  ) ||
                  get(
                    filter(
                      get(detectionEventGroup, ["events"]),
                      (e) => get(e, ["type", "id"]) === 88
                    ),
                    [0]
                  );
                let instructionColor = getLukeActionColor(
                  getAttributeValue(lukeAction, 159)
                );
                group[name === driveInOut ? driver : human].verification = [
                  {
                    verification: COVID_VERIFICATION,
                    survey: covidSurvey,
                    status:
                      trueResponse.length === 0
                        ? VERIFICATION_OK
                        : VERIFICATION_WARNING,
                    instruction_id: getAttributeValue(lukeAction, 158),
                    instruction: getAttributeValue(
                      lukeAction,
                      158,
                      lov,
                      language
                    ),
                    instructionColor,
                  },
                ];
              } else if (preSurvey && preSurvey.length > 0) {
                let lukeAction = get(
                  filter(
                    get(detectionEventGroup, ["events"]),
                    (e) => get(e, ["type", "id"]) === 88
                  ),
                  [0]
                );
                let instructionColor, instruction;
                if (lukeAction) {
                  instructionColor = getLukeActionColor(
                    getAttributeValue(lukeAction, 159)
                  );
                  instruction = getAttributeValue(
                    lukeAction,
                    158,
                    lov,
                    language
                  );
                }
                group[name === driveInOut ? driver : human].verification = [
                  {
                    surveyIdToFetch: get(preSurvey, [0, "reference_event_id"]),
                    instruction,
                    instruction_id: getAttributeValue(lukeAction, 158),
                    instructionColor,
                  },
                ];
              } else {
                let lukeAction = get(
                  filter(
                    get(detectionEventGroup, ["events"]),
                    (e) => get(e, ["type", "id"]) === 88
                  ),
                  [0]
                );
                let instructionColor, instruction;
                if (lukeAction) {
                  instructionColor = getLukeActionColor(
                    getAttributeValue(lukeAction, 159)
                  );
                  instruction = getAttributeValue(
                    lukeAction,
                    158,
                    lov,
                    language
                  );
                  group[name === driveInOut ? driver : human].verification = [
                    {
                      instruction,
                      instruction_id: getAttributeValue(lukeAction, 158),
                      instructionColor,
                    },
                  ];
                } else {
                  group[name === driveInOut ? driver : human].verification = [];
                }
              }
            }
          }
        } else if (get(detectionEventGroup, ["type", "id"]) === 23) {
          if (name === trainInOut) {
            group.detections["railcars"].push(detectionEventGroup);
          }
        } else {
          if (name === trainInOut) {
            // Everything else 
            group.detections["others"].push(detectionEventGroup);
          }
        }
      }
    );
  }
  return group;
};

export const getReferenceSurvey = async (
  surveyId,
  lov,
  language,
  existingInstruction,
  existingInstructionColor
) => {
  try {
    let survey = await fetchGet(`events/${surveyId}`);
    let verificationEventGroup = await fetchPost("new/event_groups", {
      filter: {
        ids: [get(survey, ["group", "id"])],
      },
      data: eventGroupsData,
    });
    verificationEventGroup = get(verificationEventGroup, ["items", 0]);
    survey = get(
      filter(
        get(verificationEventGroup, ["events"]),
        (e) => get(e, ["type", "id"]) === 81
      ),
      [0]
    );

    let trueResponse = get(survey, ["attributes"]).filter(
      (attr) => attr.value === true
    );
    let res = {
      reference: true,
      verification: COVID_VERIFICATION,
      survey,
      status:
        trueResponse.length === 0 ? VERIFICATION_OK : VERIFICATION_WARNING,
      //instructionColor,
    };
    if (!existingInstruction) {
      //res.instruction = getAttributeValue(lukeAction, 158, lov, language);
      //res.instructionColor = instructionColor;
    } else {
      res.instruction = existingInstruction;
      res.instructionColor = existingInstructionColor;
    }
    return res;
  } catch (e) {
    console.log(e);
  }
};

export const addPictures = (name, group) => {
  if (group.detections) {
    Object.keys(group.detections).forEach((key) => {
      if (group.detections[key].events) {
        group.detections[key].events.forEach((event) => {
          if (event.active) {
            // Human Detection
            if (get(event, ["type", "id"]) === 1 && get(event, ["images"])) {
              group[name === driveInOut ? driver : human].contextPhoto =
                event.images;
            }
            // Vehicle detection
            if (
              [65, 66, 80].includes(get(event, ["type", "id"])) &&
              get(event, ["images"])
            ) {
              if (group.truck.contextPhoto) {
                group.truck.contextPhoto.push(...event.images);
              } else {
                group.truck.contextPhoto = event.images;
              }
            }
            // Trailer detection
            if (
              [67, 68, 79].includes(get(event, ["type", "id"])) &&
              get(event, ["images"])
            ) {
              if (group.trailer1.contextPhoto) {
                group.trailer1.contextPhoto.push(...event.images);
                group.trailer1.contextPhoto.reverse();
              } else {
                group.trailer1.contextPhoto = event.images;
              }
            }
          }
        });
      }
    });
  }
  return group;
};

export const fetchOverridenByUsers = async (eventGroups) => {
  try {
    for (let i = 0; i < eventGroups.length; i++) {
      let types = [human, driver, truck, trailer1];
      for (let j = 0; j < types.length; j++) {
        if (get(eventGroups, [i, types[j], "overridenBy"])) {
          let overridenBy = await getUser(
            get(eventGroups, [i, types[j], "overridenBy"])
          );
          if (
            get(overridenBy, ["profile", "firstname"]) &&
            get(overridenBy, ["profile", "lastname"])
          ) {
            eventGroups[i][types[j]].overridenBy = `${get(overridenBy, [
              "profile",
              "firstname",
            ])} ${overridenBy.profile.lastname}`;
          }
        }
      }
    }
    return eventGroups;
  } catch (e) {
    console.log(e);
  }
};

export const getVerificationStatus = (survey) => {
  let attr151 = get(
    filter(get(survey, ["attributes"]), (attr) => attr.id === 151),
    [0]
  );
  if (get(attr151, ["value"])) {
    attr151 = JSON.parse(get(attr151, ["value"]));
    if (get(attr151, ["survey_state", "yes"]) > 0) {
      return VERIFICATION_THREAT;
    } else if (
      get(attr151, ["survey_instructions", "completed"]) &&
      get(attr151, ["survey_state", "other"]) === 0
    ) {
      return VERIFICATION_OK;
    }
    return VERIFICATION_WARNING;
  }
  return null;

  // if (!survey || !get(survey, ["attributes"])) {
  //   return null;
  // }
  // if (
  //   get(survey, ["attributes"]).filter((attr) => attr.value === true).length > 0
  // ) {
  //   return VERIFICATION_THREAT;
  // }
  // if (
  //   get(survey, ["attributes"]).filter(
  //     (attr) => attr.required && attr.value === null
  //   ).length > 0
  // ) {
  //   return VERIFICATION_WARNING;
  // }
  // return VERIFICATION_OK;
};

// Return [actionId, actionString, actionColor]
export const getVerificationAction = (questions, answers, isThreat) => {
  if (!questions || !answers) {
    return [null, null, null, null];
  }
  let actionId, actionString, actionColor, actionColorId;
  let ok = true;
  forEach(questions, (q) => {
    let ans = get(answers, [q.sequence]);
    if (q.is_true_negative_state && ans === true) {
      ok = false;
    }
  });

  if (ok === false) {
    actionId = 89;
    actionString = "nurse";
    actionColor = "FF0000";
    actionColorId = 2;
  } else if (size(answers) < size(questions)) {
    actionId = 89;
    actionString = "nurse";
    actionColor = "FFA500";
    actionColorId = 3;
  } else {
    actionId = 88;
    actionString = "door";
    actionColor = "00FF00";
    actionColorId = 0;
  }
  if (isThreat) {
    actionColor = "FF0000";
    actionColorId = 2;
  }
  return [actionId, actionString, actionColor, actionColorId];
};

export const getOverrideEvent = async (eventGroup) => {
  let event = filter(
    get(eventGroup, ["events"]),
    (e) => get(e, ["type", "id"]) === 64
  );
  if (size(event) > 0) {
    event = event.sort((a, b) => (a.created_on > b.created_on ? -1 : 1))[0];
    if (event.created_by) {
      let user = await getUser(event.created_by);
      event.overriden_by = `${get(user, ["profile", "firstname"])} ${get(user, [
        "profile",
        "lastname",
      ])}`;
    }
    return event;
  }
  return null;
};

export const getSiteZone = (eg) => {
  let site, zone;
  let count = 0;
  while ((!site || !zone) && size(get(eg, ["events"])) > count) {
    site = get(eg, ["events", count, "zone", "site", "name"]);
    zone = get(eg, ["events", count, "zone", "name"]);
    count++;
  }
  return [site, zone];
};

// Find event or event_group in an array of event groups tree
export const findEvent = (egArray, id) => {
  let result;
  forEach(egArray, (eg) => {
    if (get(eg, ["id"]) === id || get(eg, ["id"]) === parseInt(id)) {
      result = eg;
    }
    let events = findEvent(get(eg, ["events"]), id);
    let eventGroups = findEvent(get(eg, ["childEventGroups"]), id);
    if (events) result = events;
    if (eventGroups) result = eventGroups;
  });
  return result;
};

// Get instruction color
export const getLukeActionColor = (attr159) => {
  if (!attr159) return null;

  let json = JSON.parse(attr159);
  if (json) {
    let color = get(json, ["color"]);
    if (["00FF00", "00ff00"].includes(color)) {
      return colors.cardGreen;
    } else if (["FF0000", "ff0000"].includes(color)) {
      return colors.threatRed;
    } else if (["FFA500", "ffa500"].includes(color)) {
      return colors.warningOrange;
    } else if (["FFFF00", "ffff00"].includes(color)) {
      return colors.unknownYellow;
    }
  }
  return null;
};
